export type CueKind = 'voice' | 'shooter-voice' | 'fight-voice' | 'announcer' | 'effect' | 'music';

export interface PlayOptions {
  delaySeconds?: number;
  gain?: number;
  kind?: CueKind;
  playbackRate?: number;
}

type SequenceCue = string | { url: string; playbackRate?: number };

const makeVoiceCurve = (drive: number) => new Float32Array(256).map((_, index) => {
  const x = (index / 255) * 2 - 1;
  return Math.tanh(x * drive) / Math.tanh(drive);
});
const announcerCurve = makeVoiceCurve(1.35);
const characterCurve = makeVoiceCurve(1.72);
const MAX_OUTPUT_GAIN = 0.85;
const AUDIO_VERSION = '20260716-8';

export const audioAsset = (path: string) => `${path}?v=${AUDIO_VERSION}`;

class AttractAudioEngine {
  private context: AudioContext | null = null;
  private masterInput: WaveShaperNode | null = null;
  private decoded = new Map<string, Promise<AudioBuffer>>();
  private sceneSources = new Set<AudioBufferSourceNode>();
  private sourceRates = new Map<AudioBufferSourceNode, { baseRate: number; kind: CueKind }>();
  private sourceGains = new Map<AudioBufferSourceNode, { baseGain: number; gain: GainNode; kind: CueKind; kindMix: number }>();
  private manualVoice: AudioBufferSourceNode | null = null;
  private manualVoiceUrl: string | null = null;
  private manualVoiceEnded: (() => void) | null = null;
  private manualRequest = 0;
  private musicSource: AudioBufferSourceNode | null = null;
  private wantedMusicUrl: string | null = null;
  private musicRequest = 0;
  private pendingSceneCue: (() => void) | null = null;
  private enabled = false;
  private unlocked = false;
  private listenersArmed = false;
  private playbackMultiplier = 1;
  private characterVoiceVolume = 1;
  private characterVoiceSpeed = 1.2;
  private shooterVoiceVolume = 1;
  private shooterVoiceSpeed = 1.2;
  private fightVoiceVolume = 1;
  private fightVoiceSpeed = 1;
  private musicVolume = 1;

  constructor() {
    document.addEventListener('visibilitychange', this.handleVisibility);
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
    if (!enabled) {
      this.stopScene();
      this.stopMusic();
      return;
    }
    this.armUnlockListeners();
    void this.tryUnlock();
  }

  requestScene(cue: () => void) {
    this.stopScene();
    this.pendingSceneCue = cue;
    if (this.unlocked && !document.hidden) {
      this.pendingSceneCue = null;
      cue();
      return;
    }
    this.armUnlockListeners();
    void this.tryUnlock();
  }

  playIfUnlocked(url: string, options: PlayOptions = {}) {
    if (!this.enabled || !this.unlocked || document.hidden) return;
    void this.play(url, options);
  }

  async play(url: string, options: PlayOptions = {}) {
    const context = this.getContext();
    const buffer = await this.load(url);
    if (!this.enabled || !this.unlocked || document.hidden) return 0;
    const source = context.createBufferSource();
    source.buffer = buffer;
    const kind = options.kind ?? 'effect';
    const baseRate = options.playbackRate ?? 1;
    source.playbackRate.value = this.effectiveRate(baseRate, kind);
    this.sourceRates.set(source, { baseRate, kind });
    this.connect(source, kind, options.gain ?? 1);
    source.onended = () => { this.sceneSources.delete(source); this.sourceRates.delete(source); this.sourceGains.delete(source); };
    this.sceneSources.add(source);
    source.start(context.currentTime + Math.max(0, options.delaySeconds ?? 0));
    return buffer.duration;
  }

  async playSequence(cues: SequenceCue[], options: PlayOptions & { gapSeconds?: number } = {}) {
    const context = this.getContext();
    const normalized = cues.map((cue) => typeof cue === 'string' ? { url: cue, playbackRate: options.playbackRate ?? 1 } : { playbackRate: 1, ...cue });
    const buffers = await Promise.all(normalized.map((cue) => this.load(cue.url)));
    if (!this.enabled || !this.unlocked || document.hidden) return;
    let startAt = context.currentTime + Math.max(0, options.delaySeconds ?? 0);
    buffers.forEach((buffer, index) => {
      const source = context.createBufferSource();
      source.buffer = buffer;
      const baseRate = normalized[index].playbackRate ?? 1;
      const kind = options.kind ?? 'announcer';
      const playbackRate = this.effectiveRate(baseRate, kind);
      source.playbackRate.value = playbackRate;
      this.sourceRates.set(source, { baseRate, kind });
      this.connect(source, kind, options.gain ?? 1);
      source.onended = () => { this.sceneSources.delete(source); this.sourceRates.delete(source); this.sourceGains.delete(source); };
      this.sceneSources.add(source);
      source.start(startAt);
      startAt += buffer.duration / playbackRate + (options.gapSeconds ?? 0.08);
    });
  }

  prime(urls: string[]) {
    urls.forEach((url) => { void this.load(url); });
  }

  setPlaybackMultiplier(multiplier: number) {
    this.playbackMultiplier = Math.max(1, Math.min(4, multiplier));
    if (!this.context) return;
    const now = this.context.currentTime;
    this.sourceRates.forEach(({ baseRate, kind }, source) => source.playbackRate.setValueAtTime(this.effectiveRate(baseRate, kind), now));
  }

  setCharacterVoicePreferences(volume: number, speed: number) {
    this.characterVoiceVolume = Math.max(0, Math.min(1.5, volume));
    this.characterVoiceSpeed = Math.max(0.75, Math.min(2, speed));
    this.refreshVoiceMix('voice');
  }

  setShooterVoicePreferences(volume: number, speed: number) {
    this.shooterVoiceVolume = Math.max(0, Math.min(1.5, volume));
    this.shooterVoiceSpeed = Math.max(0.75, Math.min(2, speed));
    this.refreshVoiceMix('shooter-voice');
  }

  setFightVoicePreferences(volume: number, speed: number) {
    this.fightVoiceVolume = Math.max(0, Math.min(1.5, volume));
    this.fightVoiceSpeed = Math.max(0.75, Math.min(2, speed));
    this.refreshVoiceMix('fight-voice');
  }

  setMusicVolume(volume: number) {
    this.musicVolume = Math.max(0, Math.min(1, volume));
    if (!this.context) return;
    const now = this.context.currentTime;
    this.sourceGains.forEach((metadata) => {
      if (metadata.kind === 'music') metadata.gain.gain.setValueAtTime(metadata.baseGain * metadata.kindMix * this.musicVolume, now);
    });
  }

  setMusic(url: string | null) {
    if (this.wantedMusicUrl === url && (url === null || this.musicSource !== null)) return;
    this.wantedMusicUrl = url;
    this.musicRequest += 1;
    this.stopMusic();
    if (url) void this.startMusic(url, this.musicRequest);
  }

  async toggleManualVoice(url: string, onEnded: () => void) {
    const shouldStop = this.manualVoiceUrl === url && this.manualVoice !== null;
    this.stopManualVoice();
    this.stopAutomaticSources();
    if (shouldStop) return false;

    const request = ++this.manualRequest;
    await this.tryUnlock();
    const buffer = await this.load(url);
    if (request !== this.manualRequest || !this.enabled || !this.unlocked || document.hidden) return false;
    const context = this.getContext();
    const source = context.createBufferSource();
    source.buffer = buffer;
    this.sourceRates.set(source, { baseRate: 1, kind: 'voice' });
    source.playbackRate.value = this.effectiveRate(1, 'voice');
    this.connect(source, 'voice', 1);
    this.manualVoice = source;
    this.manualVoiceUrl = url;
    this.manualVoiceEnded = onEnded;
    source.onended = () => {
      if (this.manualVoice !== source) return;
      this.manualVoice = null;
      this.manualVoiceUrl = null;
      this.sourceRates.delete(source);
      this.sourceGains.delete(source);
      const ended = this.manualVoiceEnded;
      this.manualVoiceEnded = null;
      ended?.();
    };
    source.start();
    return true;
  }

  stopManualVoice() {
    this.manualRequest += 1;
    const source = this.manualVoice;
    const ended = this.manualVoiceEnded;
    this.manualVoice = null;
    this.manualVoiceUrl = null;
    this.manualVoiceEnded = null;
    if (source) {
      this.sourceRates.delete(source);
      this.sourceGains.delete(source);
      source.onended = null;
      try { source.stop(); } catch { /* source may already have ended */ }
    }
    ended?.();
  }

  stopScene() {
    this.stopManualVoice();
    this.stopAutomaticSources();
  }

  private stopAutomaticSources() {
    this.sceneSources.forEach((source) => {
      this.sourceRates.delete(source);
      this.sourceGains.delete(source);
      try { source.stop(); } catch { /* source may already have ended */ }
    });
    this.sceneSources.clear();
    this.pendingSceneCue = null;
  }

  private getContext() {
    this.context ??= new AudioContext({ latencyHint: 'interactive' });
    return this.context;
  }

  private effectiveRate(baseRate: number, kind: CueKind) {
    const voiceRate = kind === 'voice' ? this.characterVoiceSpeed : kind === 'shooter-voice' ? this.shooterVoiceSpeed : kind === 'fight-voice' ? this.fightVoiceSpeed : 1;
    return Math.max(0.5, Math.min(4.5, baseRate * voiceRate * this.playbackMultiplier));
  }

  private async load(url: string) {
    let pending = this.decoded.get(url);
    if (!pending) {
      pending = fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(`Audio asset failed: ${url}`);
          return response.arrayBuffer();
        })
        .then((data) => this.getContext().decodeAudioData(data));
      this.decoded.set(url, pending);
      pending.catch(() => this.decoded.delete(url));
    }
    return pending;
  }

  private connect(source: AudioBufferSourceNode, kind: CueKind, gainValue: number) {
    const context = this.getContext();
    const gain = context.createGain();
    const compressor = context.createDynamicsCompressor();
    const kindMix = kind === 'effect' ? 0.55 : kind === 'music' ? 0.24 : 1;
    const baseGain = Math.min(1, Math.max(0, gainValue));
    gain.gain.value = baseGain * kindMix * this.voiceVolume(kind);
    this.sourceGains.set(source, { baseGain, gain, kind, kindMix });
    compressor.threshold.value = -20;
    compressor.knee.value = 16;
    compressor.ratio.value = 4;
    compressor.attack.value = 0.005;
    compressor.release.value = 0.18;

    if (kind === 'voice' || kind === 'shooter-voice' || kind === 'announcer') {
      const highpass = context.createBiquadFilter();
      const lowpass = context.createBiquadFilter();
      const shaper = context.createWaveShaper();
      highpass.type = 'highpass';
      highpass.frequency.value = kind === 'announcer' ? 95 : 145;
      lowpass.type = 'lowpass';
      lowpass.frequency.value = kind === 'announcer' ? 4300 : 3700;
      shaper.curve = kind === 'announcer' ? announcerCurve : characterCurve;
      shaper.oversample = '2x';
      const master = this.getMasterInput();
      source.connect(highpass).connect(lowpass).connect(shaper).connect(compressor).connect(gain).connect(master);
      if (kind === 'announcer') {
        const firstDelay = context.createDelay(0.8);
        const secondDelay = context.createDelay(0.8);
        const echoFilter = context.createBiquadFilter();
        const firstEcho = context.createGain();
        const secondEcho = context.createGain();
        firstDelay.delayTime.value = 0.19;
        secondDelay.delayTime.value = 0.23;
        echoFilter.type = 'lowpass';
        echoFilter.frequency.value = 2450;
        firstEcho.gain.value = 0.24;
        secondEcho.gain.value = 0.13;
        shaper.connect(firstDelay).connect(echoFilter).connect(firstEcho).connect(master);
        firstDelay.connect(secondDelay).connect(secondEcho).connect(master);
      }
      return;
    }
    source.connect(compressor).connect(gain).connect(this.getMasterInput());
  }

  private voiceVolume(kind: CueKind) {
    return kind === 'voice' ? this.characterVoiceVolume : kind === 'shooter-voice' ? this.shooterVoiceVolume : kind === 'fight-voice' ? this.fightVoiceVolume : kind === 'music' ? this.musicVolume : 1;
  }

  private refreshVoiceMix(kind: 'voice' | 'shooter-voice' | 'fight-voice') {
    if (!this.context) return;
    const now = this.context.currentTime;
    this.sourceRates.forEach((metadata, source) => {
      if (metadata.kind === kind) source.playbackRate.setValueAtTime(this.effectiveRate(metadata.baseRate, kind), now);
    });
    this.sourceGains.forEach((metadata) => {
      if (metadata.kind === kind) metadata.gain.gain.setValueAtTime(metadata.baseGain * metadata.kindMix * this.voiceVolume(kind), now);
    });
  }

  private getMasterInput() {
    if (this.masterInput) return this.masterInput;
    const context = this.getContext();
    const limiter = context.createWaveShaper();
    const master = context.createGain();
    limiter.curve = new Float32Array([-1, 1]);
    limiter.oversample = 'none';
    master.gain.value = MAX_OUTPUT_GAIN;
    limiter.connect(master).connect(context.destination);
    this.masterInput = limiter;
    return limiter;
  }

  private armUnlockListeners() {
    if (this.listenersArmed || this.unlocked) return;
    this.listenersArmed = true;
    window.addEventListener('pointerdown', this.unlockFromGesture, { capture: true });
    window.addEventListener('keydown', this.unlockFromGesture, { capture: true });
    window.addEventListener('touchstart', this.unlockFromGesture, { capture: true, passive: true });
  }

  private removeUnlockListeners() {
    if (!this.listenersArmed) return;
    this.listenersArmed = false;
    window.removeEventListener('pointerdown', this.unlockFromGesture, { capture: true });
    window.removeEventListener('keydown', this.unlockFromGesture, { capture: true });
    window.removeEventListener('touchstart', this.unlockFromGesture, { capture: true });
  }

  private unlockFromGesture = () => { void this.tryUnlock(); };

  private async tryUnlock() {
    if (!this.enabled || document.hidden) return;
    const context = this.getContext();
    try {
      await context.resume();
    } catch {
      return;
    }
    if (context.state !== 'running') return;
    this.unlocked = true;
    this.removeUnlockListeners();
    if (this.wantedMusicUrl) void this.startMusic(this.wantedMusicUrl, this.musicRequest);
    const cue = this.pendingSceneCue;
    this.pendingSceneCue = null;
    cue?.();
  }

  private handleVisibility = () => {
    if (!this.context) return;
    if (document.hidden) {
      void this.context.suspend();
    } else if (this.enabled && this.unlocked) {
      void this.context.resume();
      if (this.wantedMusicUrl && !this.musicSource) void this.startMusic(this.wantedMusicUrl, this.musicRequest);
    }
  };

  private async startMusic(url: string, request: number) {
    if (this.musicSource || !this.enabled || !this.unlocked || document.hidden) return;
    try {
      const buffer = await this.load(url);
      if (request !== this.musicRequest || this.wantedMusicUrl !== url || this.musicSource || !this.enabled || document.hidden) return;
      const context = this.getContext();
      const source = context.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      this.sourceRates.set(source, { baseRate: 1, kind: 'music' });
      source.playbackRate.value = this.effectiveRate(1, 'music');
      this.connect(source, 'music', 1);
      source.onended = () => {
        this.sourceRates.delete(source);
        this.sourceGains.delete(source);
        if (this.musicSource === source) this.musicSource = null;
      };
      this.musicSource = source;
      source.start();
    } catch {
      // Lore scenes remain usable if the optional score cannot be decoded.
    }
  }

  private stopMusic() {
    const source = this.musicSource;
    this.musicSource = null;
    if (!source) return;
    this.sourceRates.delete(source);
    this.sourceGains.delete(source);
    source.onended = null;
    try { source.stop(); } catch { /* source may already have ended */ }
  }
}

export const attractAudio = new AttractAudioEngine();
